import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getUTMParams } from '../utils/utm';
import i18n from '../i18n/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const getPrivacyPolicyUrl = () => {
    const urls = {
      'ru': 'https://estate-barcelona.com/terms-of-use',
      'en': 'https://estate-barcelona.com/en/terms-of-use',
      'es': 'https://estate-barcelona.com/es/terms-of-use'
    };
    return urls[i18n.language] || urls['ru'];
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const utmParams = getUTMParams();
      const response = await axios.post(`${BACKEND_URL}/api/contacts`, {
        ...formData,
        ...utmParams,
        language: i18n.language
      });
      
      if (response.data.success) {
        toast.success(t('contact.successMessage'));
        setFormData({ name: '', phone: '', email: '', message: '' });
        
        // Track Lead event in Meta Pixel
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      }
    } catch (error) {
      toast.error(t('contact.errorMessage'));
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-2 border-blue-100">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">{t('contact.name')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t('contact.phone')} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder={t('contact.phonePlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t('contact.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="message">{t('contact.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 min-h-[120px]"
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 text-[#0052CC] border-gray-300 rounded focus:ring-[#0052CC]"
                  />
                  <label htmlFor="privacy" className="text-sm text-gray-600">
                    {t('contact.privacyPolicy')}{' '}
                    <a 
                      href={getPrivacyPolicyUrl()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#0052CC] hover:underline"
                    >
                      {t('contact.privacyPolicyLink')}
                    </a>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0052CC] hover:bg-[#003366]"
                  size="lg"
                >
                  {isSubmitting ? `${t('contact.submitButton')}...` : t('contact.submitButton')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.contactInfo')}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#0052CC]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('contact.phoneLabel')}</p>
                    <a href="tel:+34932205680" className="text-gray-600 hover:text-[#0052CC]">
                      +34(932)205-680
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#0052CC]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('contact.emailLabel')}</p>
                    <a href="mailto:office@estate-barcelona.com" className="text-gray-600 hover:text-[#0052CC]">
                      office@estate-barcelona.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#0052CC]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('contact.addressLabel')}</p>
                    <p className="text-gray-600">{t('contact.address')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map or Image */}
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_los-jardines-blanes/artifacts/422z99l5_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-10-28%20%D0%B2%2010.26.47.png"
                alt="Фасад комплекса"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;